import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ShoppingBag, Eye, X, Zap } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { cldSquare } from "../lib/img";

function QuickView({ product, onClose }) {
  const { addItem } = useCart();
  const [variant, setVariant] = useState(product.variants?.[0] || "");
  const hasVariants = (product.variants || []).length > 0;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center" data-testid={`quickview-${product.slug}`}>
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative z-10 max-h-[90vh] w-[92%] max-w-3xl overflow-y-auto border-2 border-black bg-white shadow-[10px_10px_0_#000]">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid={`quickview-close-${product.slug}`}
          className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center border-2 border-black bg-white text-black hover:bg-black hover:text-white"
        >
          <X size={16} />
        </button>
        <div className="grid gap-6 sm:grid-cols-2">
          <div className="aspect-square overflow-hidden border-r-0 border-b-2 border-black sm:border-b-0 sm:border-r-2">
            <img src={cldSquare(product.images?.[0], 800)} alt={product.name} className="h-full w-full object-cover" />
          </div>
          <div className="flex flex-col p-6">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">{product.category_slug.replace(/-/g, " ")}</p>
            <h3 className="mt-2 font-display text-2xl font-black tracking-tighter text-black">{product.name}</h3>
            <div className="mt-4 flex items-baseline gap-3">
              <span className="text-2xl font-black text-black">${product.price.toFixed(2)}</span>
              {product.compare_at_price && <span className="text-sm text-zinc-400 line-through">${product.compare_at_price.toFixed(2)}</span>}
            </div>
            <p className="mt-4 line-clamp-4 text-sm leading-relaxed text-zinc-600">{product.description}</p>

            {hasVariants && (
              <div className="mt-5">
                <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">{product.variant_label || "Option"} · <span className="text-black">{variant}</span></p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {product.variants.map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setVariant(v)}
                      data-testid={`quickview-variant-${v.replace(/[^a-z0-9]/gi,'')}`}
                      className={`border-2 px-3 py-1 text-[11px] font-bold uppercase tracking-widest ${variant === v ? "border-black bg-black text-white" : "border-black/20 text-black hover:border-black"}`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 flex flex-col gap-2">
              <button
                onClick={() => { addItem(product, 1, hasVariants ? variant : ""); toast.success(`${product.name} added to cart`); }}
                data-testid={`quickview-add-to-cart-${product.slug}`}
                className="w-full border-2 border-black bg-white py-3 text-xs font-bold uppercase tracking-[0.25em] text-black hover:bg-black hover:text-white"
              >
                <ShoppingBag size={13} className="mr-2 inline" /> Add To Cart
              </button>
              <Link
                to={`/${product.category_slug}/${product.slug}`}
                data-testid={`quickview-details-${product.slug}`}
                className="w-full border-2 border-black bg-black py-3 text-center text-xs font-bold uppercase tracking-[0.25em] text-white hover:bg-white hover:text-black"
              >
                <Zap size={13} className="mr-2 inline" /> View Full Details
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export const ProductCard = ({ product, index = 0, dark = false }) => {
  const { addItem } = useCart();
  const { isWishlisted, toggle } = useWishlist();
  const [quickOpen, setQuickOpen] = useState(false);
  const liked = isWishlisted(product.slug);
  const raw = product.images?.[0];
  const s600 = cldSquare(raw, 600);
  const s1200 = cldSquare(raw, 1200);

  return (
    <>
      <div
        className={`group relative flex flex-col border-2 transition-transform duration-300 ease-out will-change-transform hover:-translate-y-1 ${dark ? "border-white bg-black hover:shadow-[6px_6px_0_#fff]" : "border-black bg-white hover:shadow-[6px_6px_0_#000]"}`}
        data-testid={`product-card-${product.slug}`}
      >
        {/* Wishlist heart — top right */}
        <button
          type="button"
          aria-label={liked ? "Remove from wishlist" : "Add to wishlist"}
          onClick={(e) => { e.preventDefault(); toggle(product.slug); }}
          data-testid={`card-wishlist-${product.slug}`}
          className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center border-2 border-black bg-white text-black transition-colors duration-200 hover:bg-black hover:text-white"
        >
          <Heart size={14} className={liked ? "fill-[#1e40af] text-[#1e40af]" : ""} />
        </button>

        <Link to={`/${product.category_slug}/${product.slug}`} className="block">
          <div className={`aspect-square overflow-hidden border-b-2 ${dark ? "border-white" : "border-black"}`}>
            <img
              src={s600}
              srcSet={raw && s600 !== s1200 ? `${s600} 600w, ${s1200} 1200w` : undefined}
              sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 300px"
              alt={product.name}
              width="1200"
              height="1200"
              loading={index < 4 ? "eager" : "lazy"}
              fetchPriority={index < 2 ? "high" : undefined}
              decoding={index < 4 ? "sync" : "async"}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          </div>
        </Link>

        <div className="flex flex-1 flex-col p-5">
          <Link to={`/${product.category_slug}/${product.slug}`} className="block">
            <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">{product.category_slug.replace(/-/g, " ")}</p>
            <h3 className={`mt-2 line-clamp-2 min-h-[42px] font-display text-sm font-bold ${dark ? "text-white" : "text-black"}`}>{product.name}</h3>
            <div className="mt-3 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 tabular-nums">
              <span className={`text-base font-extrabold sm:text-lg ${dark ? "text-white" : "text-black"}`}>${product.price.toFixed(2)}</span>
              {product.compare_at_price ? (
                <span className="text-xs text-zinc-500 line-through sm:text-sm">${product.compare_at_price.toFixed(2)}</span>
              ) : null}
            </div>
          </Link>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); setQuickOpen(true); }}
              data-testid={`card-quickview-${product.slug}`}
              className={`flex-1 border-2 py-2.5 text-[10px] font-bold uppercase tracking-widest transition-colors duration-200 ${dark ? "border-white bg-black text-white hover:bg-white hover:text-black" : "border-black bg-white text-black hover:bg-black hover:text-white"}`}
            >
              Quick View
            </button>
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); addItem(product, 1, product.variants?.[0] || ""); toast.success(`${product.name} added to cart`); }}
              data-testid={`card-addtocart-${product.slug}`}
              className={`flex-1 border-2 py-2.5 text-[10px] font-bold uppercase tracking-widest transition-colors duration-200 ${dark ? "border-white bg-white text-black hover:bg-transparent hover:text-white" : "border-black bg-black text-white hover:bg-white hover:text-black"}`}
            >
              Add to Cart
            </button>
          </div>
        </div>
      </div>

      {quickOpen && <QuickView product={product} onClose={() => setQuickOpen(false)} />}
    </>
  );
};
